import { controlApiFetch } from "@/transport/control-api"
import { jsonObject, jsonString } from "@/transport/decode"
import { expectJson, expectResponse } from "@/transport/http"

export class PushOwnershipConflict extends Error {}

export async function getPushConfiguration(signal?: AbortSignal) {
  const response = await controlApiFetch("/api/push/config", { signal })
  return expectJson(response, (value) => {
    const config = jsonObject(value, "Push configuration is unavailable")
    const appOrigin = jsonString(config.appOrigin)
    if (appOrigin !== location.origin) throw new Error("This push service belongs to another app origin. Check the deployment configuration.")
    return { publicKey: jsonString(config.publicKey), appOrigin, audience: jsonString(config.audience) }
  }, { error: "Kettle push service is unavailable" })
}

export async function registerPushSubscription(body: unknown, signal?: AbortSignal) {
  const response = await controlApiFetch("/api/push/subscriptions", {
    method: "POST",
    signal,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  if (response.status === 409) throw new PushOwnershipConflict("Push subscription belongs to a previous account")
  return expectJson(response, (value) => {
    const result = jsonObject(value, "Server returned an invalid push subscription")
    return result.bindingToken === undefined ? undefined : jsonString(result.bindingToken)
  }, { error: "Could not register this device with your Kettle account" })
}

export async function pushDeviceStatus(endpoint: string, signal?: AbortSignal) {
  const response = await controlApiFetch("/api/push/status", { method: "POST", signal, headers: { "Content-Type": "application/json" }, body: JSON.stringify({ endpoint }) })
  return expectJson(response, (value) => {
    const data = jsonObject(value)
    return { status: jsonString(data.status), error: typeof data.error === "string" ? data.error : "" }
  }, { error: "Could not check background push delivery" })
}

export async function testBackgroundPush(endpoint: string, signal?: AbortSignal) {
  const response = await controlApiFetch("/api/push/test", { method: "POST", signal, headers: { "Content-Type": "application/json" }, body: JSON.stringify({ endpoint }) })
  return expectJson(response, (value) => jsonString(jsonObject(value).id), { error: "Could not queue background push test" })
}

export async function acknowledgePushNotification(input: { endpoint: string; machineName: string; id: string }, signal: AbortSignal) {
  await expectResponse(await controlApiFetch("/api/push/ack", {
    method: "POST", signal, headers: { "Content-Type": "application/json" }, body: JSON.stringify(input),
  }), { error: "Could not acknowledge foreground notification" })
}

export async function detachPushSubscription(endpoint: string, signal?: AbortSignal) {
  await expectResponse(
    await controlApiFetch("/api/push/subscriptions", {
      method: "DELETE",
      signal,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ endpoint }),
    }),
    { error: "Could not detach push device" },
  )
}
